import { Chip, TextField } from '@chronosphereio/chrono-ui';
import { Autocomplete, AutocompleteRenderGetTagProps } from '@mui/material';
import { SyntheticEvent, useCallback, useMemo, useState } from 'react';
import { escapeColons, stringToVariableOption, VariableOption } from '../../core';
import { OptionsEditorProps, VariablePlugin } from '../../plugin-system';

export type StaticListOption = string | VariableOption;

export type StaticListVariableOptions = {
  values: StaticListOption[];
};

function StaticListVariableOption({ value, ...props }: ReturnType<AutocompleteRenderGetTagProps> & { value: string }) {
  const option = stringToVariableOption(value);

  if (option.label !== option.value) {
    return (
      <Chip
        label={
          <span>
            <span style={{ fontWeight: 'bold' }}>{option.label}</span>:{option.value}
          </span>
        }
        {...props}
      />
    );
  }

  return <Chip label={option.value} {...props} />;
}

export function StaticListVariableOptionEditor(props: OptionsEditorProps<StaticListVariableOptions>) {
  // Because we are pulling out the input value state for using it during onPaste, that could cause
  // this component to rerender frequently.  Autocomplete will clear the input state when it's "value" changes, so
  // wrapping this in a usememo to prevent the value from changing to a different array on every rerender
  const value = useMemo(
    () =>
      props.value.values.map((v) => {
        if (typeof v === 'string') {
          return v;
        } else {
          if (v.value !== v.label) {
            return `${escapeColons(v.label)}:${escapeColons(v.value)}`;
          }

          return escapeColons(v.value);
        }
      }),
    [props.value.values]
  );

  const onChange = (__: unknown, value: string[]) => {
    props.onChange({
      values: value.map((v) => {
        return stringToVariableOption(v);
      }),
    });
  };

  const [inputValueState, setInputValueState] = useState('');
  const onInputChange = useCallback(
    (event: SyntheticEvent<Element>, value: string) => {
      setInputValueState(value);
    },
    [setInputValueState]
  );

  return (
    <div>
      <Autocomplete
        onPaste={(e) => {
          // Append new values on paste
          const clipboardData = e.clipboardData.getData('text/plain');

          if (clipboardData) {
            // If there is a value in the text box, append that to the front of the clipboard data
            const valueWithData = `${inputValueState}${clipboardData}`;
            const values = valueWithData.split(',');
            onChange(null, value.concat(values));
            e.preventDefault();
          }
        }}
        multiple
        value={value}
        onChange={onChange}
        options={[]}
        freeSolo
        clearOnBlur
        readOnly={props.isReadonly}
        renderTags={(values: string[], getTagProps) => {
          return values.map((value, index) => {
            return <StaticListVariableOption value={value} {...getTagProps({ index })} key={index} />;
          });
        }}
        onInputChange={onInputChange}
        renderInput={(params) => {
          return (
            <TextField
              {...params}
              label="Values"
              placeholder="Values"
              helperText='Type a value or a label:value and press "Enter" to add.'
            />
          );
        }}
      />
    </div>
  );
}

export const StaticListVariable: VariablePlugin<StaticListVariableOptions> = {
  getVariableOptions: async (spec) => {
    const values = spec.values?.map((v) => {
      if (typeof v === 'string') {
        return { label: v, value: v };
      }
      return v;
    });
    return {
      data: values,
    };
  },
  dependsOn: () => {
    return { variables: [] };
  },
  OptionsEditorComponent: StaticListVariableOptionEditor,
  createInitialOptions: () => ({ values: [] }),
};
